import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import { Ref } from 'vue'

import { explorerPlusApi } from '~/api/client'
import { ProjectSortInfo } from '~/api/repository'
import { ECOSYSTEM_QUERY_KEYS } from '~/constants/query-keys/ecosystem.constant'
import { EcosystemProjectDetailModel } from '~/types/model/ecosystem/project-detail.model'
import sort = ProjectSortInfo.sort;
import type { categoryType } from '~/types/category.type'
import type { IDefiRequestParam } from '~/types/defi.type'
import { DefiChartModel, DefiListModel } from '~/types/model/defi.model'
import { EcosystemCategoryModel } from '~/types/model/ecosystem/category-list.model'
import { EcosystemGeneralModel } from '~/types/model/ecosystem/ecosystem-general.model'
import { EcosystemSearchListModel } from '~/types/model/ecosystem/search.model'
import { EcosystemSortModel } from '~/types/model/ecosystem/sort-list.model'

export const useEcosystemProjectSearch = (term: Ref<string>, category: Ref<categoryType>, sort: Ref<sort>, page: number, size: number, isEnable?: Ref<boolean>) => {
  return useInfiniteQuery([ ...ECOSYSTEM_QUERY_KEYS.SEARCH, term, category, sort, page, size ], async ({ pageParam = page }) => {
    return explorerPlusApi.ecosystemController.getEcosystemProjectSearch({
      term: term.value,
      category: category.value,
      sort: sort.value,
      page: pageParam,
      size,
    }).then(res => new EcosystemSearchListModel(res))
  }, {
    enabled: isEnable,
    getNextPageParam: (lastPage: EcosystemSearchListModel, allPages: Array<EcosystemSearchListModel>) => {
      return lastPage?.projectSummaryList.length === size ? allPages.length + 1 : undefined
    }
  })
}

export const useEcosystemProjectDetail = (id: string) => {
  return useQuery([ ...ECOSYSTEM_QUERY_KEYS.DETAIL, id ], async (): Promise<EcosystemProjectDetailModel> => {
    return explorerPlusApi.ecosystemController.getEcosystemProjectDetail({ id }).then(res => {
      return new EcosystemProjectDetailModel(res)
    })
  })
}

export const useEcosystemProjectSortList = () => {
  return useQuery([ ...ECOSYSTEM_QUERY_KEYS.SORT_LIST ], async () => {
    return explorerPlusApi.ecosystemController.getEcosystemProjectSortList().then(res => {
      return res.map(sort => new EcosystemSortModel(sort))
    })
  })
}

export const useEcosystemProjectCategoryList = () => {
  return useQuery([ ...ECOSYSTEM_QUERY_KEYS.CATEGORY_LIST ], async () => {
    return explorerPlusApi.ecosystemController.getEcosystemProjectCategoryList().then(res => {
      return [ new EcosystemCategoryModel('All', 'ALL'), ...res.map(category => new EcosystemCategoryModel(category.CATG, category.CD)) ]
    })
  })
}

export const useEcosystemGeneral = (types: string[]) => {
  return useQuery([ ...ECOSYSTEM_QUERY_KEYS.GENERAL ], async () => {
    return explorerPlusApi.ecosystemController.getEcosystemGeneral({
      types: types.join(',')
    }).then(res => {
      return new EcosystemGeneralModel(res)
    })
  })
}

export const useGetEcosystemFeaturedDetail = async (projectId: string, featuredId: string) => {
  return useAsyncData([ ...ECOSYSTEM_QUERY_KEYS.FEATURED, projectId, featuredId ].join(''), async () => {
    return explorerPlusApi.ecosystemController.getEcosystemFeatured({
      id: projectId,
      featuredId
    })
  })
}

export const useGetDefiDashboardList = (param: Ref<IDefiRequestParam>) => {
  const getDefiDashboardList = async ({ pageParam = param.value.page }) => {
    const res = await explorerPlusApi.ecosystemController.getProtocolList({
      search: param.value.search || null,
      category: param.value.category || null,
      field: param.value.field,
      direction: param.value.direction,
      page: pageParam,
      size: param.value.size,
    })
    return new DefiListModel(res, (pageParam - 1) * param.value.size)
  }
  return useInfiniteQuery([ ...ECOSYSTEM_QUERY_KEYS.DEFI_DASHBOARD_LIST, param ], getDefiDashboardList, {
    getNextPageParam: (lastPage: DefiListModel, allPages: Array<DefiListModel>) => {
      return allPages.length * param.value.size < lastPage.totalCount ? allPages.length + 1 : undefined
    }
  })
}

export const useGetDefiCategoryList = () => {
  const getDefiCategoryList = async () => {
    const res = await explorerPlusApi.ecosystemController.getProtocolCategoryList()
    return [ { label:'All', value:'' } ].concat(res.CTGRLIST.map(category => new EcosystemCategoryModel(category, category)))
  }
  return useQuery([ ...ECOSYSTEM_QUERY_KEYS.DEFI_CATEGORY_LIST ], getDefiCategoryList)
}

export const useGetDefiChartList = () => {
  const getDefiChartList = async () => {
    const res = await explorerPlusApi.ecosystemController.getTvlChartList()
    return new DefiChartModel(res)
  }
  return useQuery([ ...ECOSYSTEM_QUERY_KEYS.DEFI_CHART ], getDefiChartList)
}
